// how the help chat shows answers, and tells the assistant where the user is
import { escapeHtml } from '../../linq-utils/formatting/html';

// bold, italics, code and links within a line. The text is escaped first, so nothing in it becomes markup except
// these; links are only to LINQ's help guides (guide:<slug>, opened in the help library) and https pages, opened in a new tab
const inline = (escaped:string) => escaped
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
    .replace(/(^|[^*])\*([^*\s][^*]*)\*/g, '$1<i>$2</i>')
    .replace(/\[([^\]]+)\]\(guide:([a-z0-9-]+)\)/g, '<a href="#/help/$2" class="guide-link">$1</a>')
    .replace(/\[([^\]]+)\]\((https:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')

// the assistant's answer, which is plain Markdown (paragraphs, bold, lists), as HTML that is safe to show
export const renderHelpAnswer = (markdown:string):string => {
    const html:string[] = []
    let list:'ul'|'ol'|undefined
    const closeList = () => {
        if (list) html.push(`</${list}>`)
        list = undefined
    }

    for (const raw of (markdown || '').replace(/\r\n/g, '\n').split('\n')) {
        const line = raw.trim()
        const bullet = line.match(/^[-*•]\s+(.*)$/)
        const numbered = line.match(/^\d+[.)]\s+(.*)$/)
        if (bullet || numbered) {
            const kind = bullet ? 'ul' : 'ol'
            if (list !== kind) {
                closeList()
                html.push(`<${kind}>`)
                list = kind
            }
            html.push(`<li>${inline(escapeHtml((bullet || numbered)![1]))}</li>`)
        }else if (!line) {
            closeList()
        }else {
            closeList()
            // headings aren't wanted in a chat, so they read as bold lines
            const heading = line.match(/^#{1,6}\s+(.*)$/)
            html.push(heading ? `<p><b>${inline(escapeHtml(heading[1]))}</b></p>` : `<p>${inline(escapeHtml(line))}</p>`)
        }
    }
    closeList()
    return html.join('')
}

// the screen the user is on, from the app's address, so the assistant can put its answer in context
export const screenForUrl = (url:string):string|undefined => {
    const path = (url || '').split(/[?#]/)[0]
    if (/\/get-started$/.test(path)) return 'getting started (creating a model with AI)'
    if (/\/models\/[^/]+\/[^/]+$/.test(path)) return path.includes('/models/organisation/') ? 'organisation model' : 'model'
    if (/\/insights$/.test(path)) return 'insights'
    if (/\/analysis$/.test(path)) return 'analysis'
    if (/\/billing$/.test(path)) return 'statistics and billing'
    if (/\/onboarding$/.test(path)) return 'learn the basics of modelling walkthrough'
    if (/\/organisations\/[^/]+\/projects\/[^/]+$/.test(path)) return 'project home'
    if (/\/organisations\/[^/]+$/.test(path)) return 'organisation home'
    if (/\/organisations$/.test(path)) return 'organisations list'
    if (/\/(administration|users)$/.test(path)) return 'administration'
    return undefined
}
